import { useState } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { SEO } from '@/components/layout/SEO'
import { PageHeader, Btn, FormField } from '@/components/ui'
import { C } from '@/constants/theme'
import { WA_NUMBER } from '@/constants/data'
import type { CartItem } from '@/types'

interface OrderForm {
  name: string
  email: string
  phone: string
  address: string
  notes: string
}

const EMPTY: OrderForm = { name: '', email: '', phone: '', address: '', notes: '' }

export default function Checkout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { items, total } = (location.state ?? {}) as { items?: CartItem[]; total?: number }

  const [form, setForm] = useState<OrderForm>(EMPTY)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  // Guard: nothing in cart
  if (!items || items.length === 0) {
    return (
      <>
        <SEO title="Checkout" description="Review and place your auto glass order." path="/checkout" />
        <PageHeader label="Order" title="Checkout" />
        <div style={{ background: C.grayLight, minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🛒</div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 12 }}>Your cart is empty</div>
            <Link to="/products" style={{ color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontSize: 14 }}>← Browse products</Link>
          </div>
        </div>
      </>
    )
  }

  const set = (key: keyof OrderForm) => (v: string) => setForm(f => ({ ...f, [key]: v }))

  const handleSubmit = () => {
    if (!form.name || !form.phone) {
      setError('Please fill in your name and phone number.')
      return
    }
    setError('')
    const itemLines = items!
      .map(i => `  • ${i.make} ${i.model} (${i.years}) — R${i.price.toLocaleString()}`)
      .join('\n')
    const lines = [
      `Hi GlassTech! I'd like to place an order.`,
      ``,
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      form.email   ? `Email: ${form.email}`     : null,
      form.address ? `Address: ${form.address}` : null,
      ``,
      `Items:`,
      itemLines,
      ``,
      `Total: R${total?.toLocaleString()}`,
      form.notes ? `\nNotes: ${form.notes}` : null,
    ].filter(l => l !== null).join('\n')
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(lines)}`, '_blank')
    setSent(true)
  }

  return (
    <>
      <SEO
        title="Checkout — Confirm Your Order"
        description="Review your cart and submit your order. GlassTech Pty Ltd will confirm your booking and arrange fitting."
        path="/checkout"
      />

      <PageHeader label="Order Confirmation" title="Checkout" />

      <div style={{ background: C.grayLight, minHeight: '80vh' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '60px 24px' }}>

          {sent ? (
            <div style={{ textAlign: 'center', padding: '80px 24px', background: C.white, border: `1px solid ${C.grayBorder}`, borderTop: `4px solid ${C.blue}` }}>
              <div style={{ fontSize: 56, marginBottom: 20 }}>💬</div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 32, fontWeight: 700, color: C.text, marginBottom: 12 }}>WhatsApp is opening!</div>
              <div style={{ color: C.textMid, fontSize: 15, lineHeight: 1.7, maxWidth: 440, margin: '0 auto' }}>
                Your order summary is pre-filled. Press <strong>Send</strong> in WhatsApp to confirm your order with GlassTech.
              </div>
              <div style={{ marginTop: 32 }}>
                <Link to="/products" style={{ color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontSize: 13, letterSpacing: 1 }}>
                  ← Back to Products
                </Link>
              </div>
            </div>
          ) : (
            <div className="gt-checkout-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 28, alignItems: 'start' }}>

              {/* Order summary */}
              <div style={{ background: C.white, border: `1px solid ${C.grayBorder}`, borderTop: `3px solid ${C.blue}`, padding: 28 }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 20 }}>Order Summary</div>

                {items.map(item => (
                  <div key={item.cartId} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${C.grayMid}` }}>
                    <div>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, color: C.text, fontSize: 14 }}>
                        {item.make} {item.model}
                      </div>
                      <div style={{ fontSize: 12, color: C.textLight }}>{item.cat} · {item.years}{item.sensor ? ' · Rain sensor' : ''}</div>
                    </div>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, color: C.blue, fontSize: 16, whiteSpace: 'nowrap', paddingLeft: 12 }}>
                      R{item.price.toLocaleString()}
                    </div>
                  </div>
                ))}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: C.textMid, letterSpacing: 1 }}>TOTAL (INCL. VAT)</div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 28, fontWeight: 700, color: C.blue }}>R{total?.toLocaleString()}</div>
                </div>

                <div style={{ marginTop: 20, padding: 14, background: C.blueLight, border: `1px solid rgba(0,174,239,0.2)`, fontSize: 12, color: C.textMid, lineHeight: 1.6 }}>
                  A GlassTech technician will contact you to confirm pricing, arrange glass sourcing, and schedule your fitting.
                </div>

                <button
                  onClick={() => navigate(-1)}
                  style={{ marginTop: 16, background: 'none', border: 'none', color: C.textLight, cursor: 'pointer', fontFamily: "'Rajdhani', sans-serif", fontSize: 12, letterSpacing: 1 }}
                >
                  ← Edit cart
                </button>
              </div>

              {/* Customer details */}
              <div style={{ background: C.white, border: `1px solid ${C.grayBorder}`, borderTop: `3px solid ${C.blue}`, padding: 28 }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 20 }}>Your Details</div>

                <FormField label="Full Name *"     value={form.name}    onChange={set('name')}    placeholder="John Smith" />
                <FormField label="Phone Number *"  value={form.phone}   onChange={set('phone')}   type="tel"   placeholder="+27 ..." />
                <FormField label="Email Address"   value={form.email}   onChange={set('email')}   type="email" placeholder="john@email.com" />
                <FormField label="Fitting Address"  value={form.address} onChange={set('address')} placeholder="Street, suburb, city (optional — for mobile fitting)" />
                <FormField label="Notes"            value={form.notes}   onChange={set('notes')}   rows={3} placeholder="Any special requests, preferred fitting times, etc." />

                {error && (
                  <div style={{ marginBottom: 16, padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', fontSize: 13 }}>
                    {error}
                  </div>
                )}

                <Btn onClick={handleSubmit}>Place Order via WhatsApp</Btn>

                <div style={{ marginTop: 14, fontSize: 12, color: C.textLight }}>
                  Or call us: <a href="tel:+27692145065" style={{ color: C.blue, textDecoration: 'none' }}>+27 69 214 5065</a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 720px) { .gt-checkout-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </>
  )
}
